import * as Sentry from "@sentry/react";
import { message } from "antd";
import { useMutation, useQueryClient } from "@tanstack/react-query";

import { SalesMeetingSaleStatus } from "@fitness-app/data-models/entities/SalesMeeting";

import { analyticsService, integrationApi, supabase } from "~/store/initializeStore";
import { ORDER_OPTION_PREFIX } from "../salesCrmConstants";
import { normalizeCycles } from "../salesCrmUtils";
import { type FeedbackForm, type LinkableOrder } from "../salesCrmTypes";
import { type SalesMeetingRow } from "./useSalesMeetingsQuery";

type UseSaveFeedbackMutationParams = {
  activeMeeting: SalesMeetingRow | null;
  orders: LinkableOrder[];
  lockedTeamMemberId: string | null;
  userId: string | null | undefined;
  onSuccess: () => void;
};

export const useSaveFeedbackMutation = ({
  activeMeeting,
  orders,
  lockedTeamMemberId,
  userId,
  onSuccess,
}: UseSaveFeedbackMutationParams) => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async (values: FeedbackForm) => {
      if (!activeMeeting) return;

      let bundleId = values.customerBundleId;

      if (bundleId?.startsWith(ORDER_OPTION_PREFIX)) {
        const orderId = bundleId.slice(ORDER_OPTION_PREFIX.length);
        const order = orders.find((row) => row.id === orderId);

        if (!order) {
          throw new Error("Nie znaleziono wybranego zamówienia");
        }

        const { data: customers, error: customerError } = await supabase
          .from("customer")
          .select("id, traineeId, userId")
          .ilike("email", order.clientEmail)
          .limit(1);

        if (customerError) throw new Error(customerError.message);
        const customer = customers?.[0] ?? null;

        const fallbackAmount = (order.amount ?? 0) / 100;
        const amount = Math.round((values.manualAmount ?? fallbackAmount) * 100);
        const totalAmount = Math.round((values.manualTotalAmount ?? values.manualAmount ?? fallbackAmount) * 100);
        // The field is prefilled from the order, so the form value already
        // carries order.cycles unless the user cleared it — respect that and
        // normalize (only >= 2 installments count as installments).
        const cycles = normalizeCycles(values.manualCycles);

        const { data: newBundle, error: bundleError } = await supabase
          .from("customer_bundle")
          .insert({
            type: "new_cart",
            clientEmail: order.clientEmail,
            name: order.productName ?? `Zamówienie ${order.id}`,
            currency: order.currency || "pln",
            amount,
            totalAmount,
            cycles,
            customerId: customer?.id ?? null,
            traineeId: customer?.traineeId ?? null,
            userId: customer?.userId ?? null,
            productId: order.product,
            initialOrderId: order.id,
            // Settlements aggregate by the bundle purchase date — use the sale
            // date from the form (prefilled with the order date) so manual
            // links land in the right settlement month.
            ...(values.manualSaleDate || order.createdAt
              ? {
                  createdAt: values.manualSaleDate?.toISOString() ?? order.createdAt,
                }
              : {}),
            metadata: {
              orderId: order.id,
              manualLink: true,
              linkedBy: userId ?? null,
              paymentStatus: order.status,
              paymentType: order.paymentType,
              orderCreatedAt: order.createdAt,
              originalCycles: cycles,
            },
          } as never)
          .select("id")
          .single();

        if (bundleError) throw new Error(bundleError.message);
        bundleId = (newBundle as { id: string }).id;
      }

      const bundleChanged = (bundleId ?? null) !== (activeMeeting.customerBundleId ?? null);
      const isNotQualified = values.saleStatus === SalesMeetingSaleStatus.NotQualified;

      let query = supabase
        .from("sales_meeting")
        .update({
          status: values.status,
          statusLocked: true,
          saleStatus: values.saleStatus,
          // Dropped when the status moves off "nie kwalifikuje się", so a stale
          // explanation can never outlive the status it explains.
          notQualifiedReason: isNotQualified ? values.notQualifiedReason?.trim() || null : null,
          customerBundleId: bundleId,
          ...(bundleId && bundleChanged ? { matchedCustomerBundleId: bundleId, matchConfidence: "manual" } : {}),
          notes: values.notes,
          feedbackAt: new Date().toISOString(),
          feedbackBy: userId || null,
          updatedAt: new Date().toISOString(),
        } as never)
        .eq("id", activeMeeting.id);

      if (lockedTeamMemberId) {
        query = query.eq("teamMemberId", lockedTeamMemberId);
      }

      const { error } = await query;

      if (error) throw new Error(error.message);

      analyticsService.track("sales_meeting_feedback_saved", {
        meetingId: activeMeeting.id,
        status: values.status,
        saleStatus: values.saleStatus,
        notQualified: isNotQualified,
        notQualifiedReasonProvided: isNotQualified && Boolean(values.notQualifiedReason?.trim()),
      });

      // Funnel stages go to Meta from the backend, which holds the CRM dataset
      // credentials. Reporting is derived from the row we just wrote and is
      // idempotent, so a failure here only delays it until the next save — it
      // must never cost the rep the feedback they just typed.
      try {
        await integrationApi.post("/calendly/sales-call/report-lead-stage", { meetingId: activeMeeting.id });
      } catch (reportError) {
        Sentry.captureException(reportError, { data: { action: "report sales meeting lead stage to Meta" } });
      }
    },
    onSuccess: async () => {
      onSuccess();
      await queryClient.invalidateQueries({ queryKey: ["salesCrmMeetings"] });
      await queryClient.invalidateQueries({ queryKey: ["salesCrmBundles"] });
      void message.success("Feedback zapisany");
    },
    onError: (error) => {
      void message.error(`Nie udało się zapisać feedbacku: ${(error as Error).message}`);
    },
  });
};
